import { useEffect, useState } from 'react'
import { logError, LogErrorMetrics } from '@lib/tracking/felog'
import { transformToString } from '@lib/utils/error'

/**
 * Detects if the current browser environment supports WebGL rendering.
 * - Runs on the client only; defaults to true until detection completes to avoid unnecessary flicker.
 * - Uses window 'load' when available to defer until styles and DOM are settled, otherwise runs on mount.
 */
export function useWebGLSupport(): boolean {
  const [isSupported, setIsSupported] = useState(true)

  useEffect(() => {
    if (!IS_CLIENT_SIDE) {
      // SSR: we cannot know yet; assume true to keep primary experience until hydrated
      return
    }

    const detect = () => {
      let canvas: HTMLCanvasElement | null = null
      let gl: WebGLRenderingContext | WebGL2RenderingContext | null = null
      try {
        canvas = document.createElement('canvas')
        // Keep it tiny and off-DOM; there is no visual impact
        canvas.width = 1
        canvas.height = 1

        gl =
          (canvas.getContext('webgl2') as WebGL2RenderingContext | null) ||
          (canvas.getContext('webgl') as WebGLRenderingContext | null) ||
          (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null)

        // If a context object is returned, WebGL is supported
        setIsSupported(!!gl)
      } catch (_err) {
        setIsSupported(false)
      } finally {
        // Proactively release GPU resources and allow GC to reclaim the canvas
        try {
          gl?.getExtension('WEBGL_lose_context')?.loseContext()
        } catch (error) {
          logError('Error calling getExtension on WebGLRenderingContext', LogErrorMetrics.WebGL, {
            error: transformToString(error as Error),
          })
        }

        if (canvas) {
          canvas.width = 0
          canvas.height = 0
          // In case it ever gets attached in the future, this will detach it safely
          if ('remove' in canvas) {
            canvas.remove()
          }
        }

        canvas = null
        gl = null
      }
    }

    if (document.readyState === 'complete') {
      // Page already loaded
      detect()
    } else {
      // Defer until full load to mirror the provided snippet's behavior
      const onLoad = () => detect()
      window.addEventListener('load', onLoad, { once: true })
      return () => window.removeEventListener('load', onLoad)
    }
  }, [])

  return isSupported
}
